import { resendEmailVerification } from '@/api/backend/account';
import { useCountdownTimer } from '@/hooks/use-countdown-timer';
import { Button, FormField, FormLabel, Link } from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type VerifyEmailContentProps = {
  email: string;
  onError?: (error: AxiosError | undefined) => void;
  autoSendInitialVerification: boolean;
  redirectUrl?: string;
  onSuccess?: () => void;
};

const RESEND_TIMEOUT = 60;

export const VerifyEmailContent = ({
  email,
  onError,
  redirectUrl,
  onSuccess,
  autoSendInitialVerification,
}: VerifyEmailContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { formatTime, isFinished, startTimer } = useCountdownTimer(RESEND_TIMEOUT);
  const resendDisabled = !isFinished;

  const { mutate: resendVerification, isPending: isResendingVerification } = useMutation({
    mutationFn: async () => {
      return await resendEmailVerification(email);
    },
    onSuccess: () => {
      startTimer();
      onError?.(undefined);
      onSuccess?.();
    },
    onError: (err: AxiosError) => {
      console.error('Error resending verification email:', err);
      onError?.(err);
    },
  });

  const handleResendVerification = () => {
    if (!resendDisabled) {
      resendVerification();
    }
  };

  const handleBackToLogin = () => {
    navigate({
      to: '/login',
      search: { redirect: redirectUrl },
    });
  };

  useEffect(() => {
    if (autoSendInitialVerification) {
      resendVerification();
    }
  }, [autoSendInitialVerification, resendVerification]);

  return (
    <>
      <div className="mb-10">
        <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('register.verifyEmailTitle')}</h1>
        <p className="text-xl text-muted-foreground">{t('register.verifyEmailDescription')}</p>

        <div className="flex flex-col items-start gap-2 my-8">
          <FormField>
            <FormLabel>{t('register.fields.email')}</FormLabel>
            <div className="text-base">{email}</div>
          </FormField>

          <div className="flex flex-col items-start gap-2 mt-4">
            <span className="font-medium">{!isFinished ? formatTime() : ''}</span>
            <span>
              {t('register.verifyEmailNotReceived')}{' '}
              <Link
                type="button"
                onClick={handleResendVerification}
                disabled={resendDisabled || isResendingVerification}
              >
                {t('register.verifyEmailResend')}
              </Link>
            </span>
          </div>
        </div>
      </div>

      <Button className="w-full" onClick={handleBackToLogin}>
        {t('register.verifyEmailButton')}
      </Button>
    </>
  );
};
